import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import Loading from '../Shared/Loader';
import If from '../If';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getBestSellerProduct } from '../../../redux/actions/customizer';
import { getFontOptions, getSelectedOption } from '../../../utils/commonRingUtils';
import { useRingCustomizerContext } from '../../context/RingCustomizerContext';
import KayJewelersLogo from '../../../assets/images/kay-logo.svg';
import KayEngravingFilter from './components/KayEngravingFilter';
import KaySizeFilter from './components/KaySizeFilter';
import PlusIcon from '../../../assets/images/plus.png';
import CloseIcon from '../../../assets/images/close-icon.svg';
import { formatSummaryDate } from '../../components/Summary';


import './index.scss';

type IProps = {
  id: string;
  handleBestSeller?: (id: string) => void;
  engraving: string;
  setEngraving: (text: string) => void;
  size: number;
  setSize: (size: number) => void;
  handleBestSellerDone: () => void;
  product: any;
  navigateToUrl: (url: string, replace?: boolean, bestSeller?: string) => void;
  onClose: () => void;
};

let timedOutId: NodeJS.Timeout | undefined;

// Helper function to parse currency string (e.g., "$1,000.00") to number
const parseCurrencyToNumber = (currencyStr: string | undefined): number => {
  if (!currencyStr) return 0;
  // Remove $ symbol and commas, then parse to float
  return parseFloat(currencyStr.replace(/[$,]/g, ''));
};

const KayDiscountedPopup = ({
  id,
  handleBestSeller,
  product,
  navigateToUrl,
  engraving,
  setEngraving,
  size,
  setSize,
  handleBestSellerDone,
  onClose
}: IProps) => {
  const { subpath } = useRingCustomizerContext();
  const { bestSellerProduct, isFetching } = useAppSelector((state) => state.customizer);
  const dispatch = useAppDispatch();
  const [isEngravingOpen, setIsEngravingOpen] = useState(false);
  const [isSizeChanged, setIsSizeChanged] = useState(false);
  const [similarProducts, setSimilarProducts] = useState<any[]>([]);
  const [, setIsSimilarLoading] = useState(false);

  const fetchBestSellerProduct = useCallback(
    async (data?: { size: number; engraving: string }) => {
      const { size: sizeArg, engraving: engravingArg } = data || {};
      try {
        const payload = {
          id,
          data: {
            size: sizeArg || '',
            engraving: engravingArg || '',
            subpath,
          }
        };
        await dispatch(getBestSellerProduct(payload)).unwrap();
      } catch (error) {
        console.log(error);
      }
    },
    [dispatch, id, subpath]
  );

  useEffect(() => {
    if (!bestSellerProduct || bestSellerProduct?.bestseller_id !== id) {
      fetchBestSellerProduct({ size: size || 10, engraving: engraving || '' });
    }
    // Intentionally omit size/engraving from deps: slider and engraving handlers call fetchBestSellerProduct; including them would refetch on every edit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestSellerProduct, fetchBestSellerProduct, id]);

  const handleClick = (id: string) => () => {
    navigateToUrl(bestSellerProduct?.ring_options_url, false, id);
  };

  const handleOnChange = (updated: any) => {
    if (updated.engraving_new?.includes('None')) {
      setEngraving('');
      fetchBestSellerProduct({ size: size, engraving: '' });
    } else {
      setEngraving(updated.engraving_new);
      fetchBestSellerProduct({ size: size, engraving: updated.engraving_new });
    }
  };

  const handleSliderChange = (value: number) => {
    setSize(value);
    setIsSizeChanged(true);
    if (timedOutId) {
      clearTimeout(timedOutId);
    }
    timedOutId = setTimeout(() => {
      fetchBestSellerProduct({ size: value, engraving: engraving });
    }, 200);
  };

  const getEngravingContent = () => {
    const content = {
      hasSwatch: false,
      fieldType: 'engraving_new',
      options: getFontOptions(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || [], 'kay-engraving-font'
      ),
      selectedOption: getSelectedOption(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      engravingText: bestSellerProduct?.engraving_new?.text?.text || ''
    };
    return (
      <div className={clsx("tw-w-full tw-bg-white", { 'tw-hidden': !isEngravingOpen, 'tw-mt-4': isEngravingOpen })}>
        <KayEngravingFilter onChange={handleOnChange} isFetching={false} content={content} />
      </div>
    );
  };

  const getRingMaterialFromUrl = (url: string): string | null => {
    if (!url) return null;

    // Match 'ring_materials/<material>' in the URL
    const match = url.match(/ring_materials\/([^?/]+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const ringmaterial = getRingMaterialFromUrl(bestSellerProduct?.ring_options_url);

    if (!ringmaterial) return;
    
    const fetchSimilarProducts = async () => {
      try {
        setIsSimilarLoading(true);

        const response = await fetch(
          `https://api.lashbrookdesigns.com/api/v2/product/getSimilar/${ringmaterial}`
        );

        const data = await response.json();

        setSimilarProducts(data?.data || data || []);
      } catch (error) {
        console.error('Error fetching similar products:', error);
      } finally {
        setIsSimilarLoading(false);
      }
    };

    fetchSimilarProducts();
  }, [bestSellerProduct?.ring_options_url]);

  return (
    <div className="xl:tw-container xl:tw-px-10 tw-px-6 tw-mx-auto tw-py-4 tw-h-full tw-overflow-hidden">
      <If condition={!!bestSellerProduct}>
        <div className="kay-header">
          <img src={KayJewelersLogo} width={130} alt="Kay Jewelers" />
          <div>
            <div className="tw-grid tw-grid-cols-2 tw-text-sm">
              <span className='tw-text-right'>Magic Number #:</span>
              <span className="tw-text-right tw-pl-2 md:tw-pl-4">
                {isSizeChanged ? (bestSellerProduct?.magic_number) : (
                  <>
                    <span className='tw-font-bold'>{bestSellerProduct?.magic_number?.slice(0,bestSellerProduct.magic_number.lastIndexOf('-'))}</span><span className="tw-text-[#DC0000] tw-ml-0">{isSizeChanged ? `-${size}` : '-Select Finger Size'}</span>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="kay-popup-content">
          <div className="left-content">
            <div className='xl:tw-w-full lg:tw-w-1/2 md:tw-w-3/4 tw-w-full tw-mx-auto'>
              <div className="image-container">
                <img
                  src={`${process.env.REACT_APP_ASSET_URL}${bestSellerProduct?.images[0]}`}
                  alt="ring"
                  className="tw-object-cover"
                />
              </div>
              {/* <video src={KayVideo} autoPlay={true} loop={true} muted={true} playsInline={true} className="tw-object-cover tw-my-6" /> */}
              {/* <div className="kay-image-sequence tw-flex tw-flex-row tw-justify-center tw-items-center tw-my-6 tw-overflow-x-auto">
                {bestSellerProduct?.image_urls_for_animation?.slice(0, 5).map((i: string, index: number) => (
                  <img
                    key={`animation-${bestSellerProduct?.bestseller_id}-${index}`}
                    src={`${process.env.REACT_APP_ASSET_URL}${i}`}
                    alt="ring"
                    className="kay-animated-image tw-object-cover tw-relative"
                    style={{
                      marginLeft: index > 0 ? '-20px' : '0',
                      zIndex: 1 + index,
                    }}
                  />
                ))}
              </div> */}
            </div>
          </div>
          <div className="right-content tw-min-w-[2px]">
            <div className="tw-text-2xl tw-font-bold tw-pb-2">
              <div className="tw-flex">
                <If condition={parseCurrencyToNumber(bestSellerProduct?.msrp) > parseCurrencyToNumber(bestSellerProduct?.price)}>
                  <span className="tw-text-brand-color tw-line-through tw-pr-2">{bestSellerProduct?.msrp}</span>
                </If>
                <span className="tw-text-primary-color">{bestSellerProduct?.price}</span>
                <If condition={parseCurrencyToNumber(bestSellerProduct?.msrp) > parseCurrencyToNumber(bestSellerProduct?.price)}>
                  <div>
                    <span style={{
                      backgroundColor: "#D7F2EE",
                      borderRadius: "100px",
                      fontSize: "12px",
                      fontWeight: "400",
                      marginLeft: "15px",
                      padding: "2px 10px"
                    }}>
                      Pre-Designed Discount
                    </span>
                  </div>
                </If>
              </div>
            </div>
            <div className="tw-text-base">{bestSellerProduct?.description}</div>
            {/* <h4 className="tw-text-base tw-font-normal tw-py-4">Ship Date: <span className='tw-font-bold'> {typeof shippingDate === 'string' ? formatSummaryDate(shippingDate) : ''}</span></h4> */}
            <h4 className="tw-text-base tw-font-normal tw-py-4">Ship Date: <span className='tw-font-bold'> {formatSummaryDate(product?.shipping_options?.standard)}</span></h4>
            <div className="divider"></div>

            <If condition={bestSellerProduct?.variants?.length > 1}>
              <h4 className="tw-text-base tw-font-bold tw-py-4">Weight</h4>
              <div className="tw-flex tw-flex-row tw-flex-wrap tw-gap-3">
                {bestSellerProduct?.variants?.map((variant: any) => (
                  <div
                    key={variant.bestseller_id}
                    onClick={!variant.selected ? handleClick(variant.bestseller_id) : undefined}
                    className={clsx('tw-text-sm kay-weight', {
                      'light-border': !variant.selected,
                      'dark-border': variant.selected
                    })}
                  >
                    {variant.name}
                  </div>
                ))}
              </div>
              <div className="divider"></div>
            </If>


            <h4 className="tw-text-base tw-font-bold tw-py-4">Finger Size:</h4>
            <KaySizeFilter options={bestSellerProduct?.finger_size || bestSellerProduct?.size || []}
              size={size} onChange={handleSliderChange} isSizeChanged={isSizeChanged} setIsSizeChanged={setIsSizeChanged} />

            <div className="divider"></div>

            {/* <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-pt-4 tw-cursor-pointer" onClick={() => setIsEngravingOpen(!isEngravingOpen)}>
              <h4 className="tw-text-base tw-font-bold">ADD AN ENGRAVING</h4>
              <img src={isEngravingOpen ? CloseIcon : PlusIcon} alt="bottom-arrow" className="tw-w-4" />
            </div>
            {getEngravingContent()} */}
            <If condition={(bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts)}>
              <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-pt-4 tw-cursor-pointer" onClick={() => setIsEngravingOpen(!isEngravingOpen)} >
                <h4 className="tw-text-base tw-font-bold">ADD AN ENGRAVING</h4>
                <img src={isEngravingOpen ? CloseIcon : PlusIcon} alt="bottom-arrow" className="tw-w-4" />
              </div>
              {getEngravingContent()}
              <div className="divider"></div>
            </If>
            <div className="divider"></div>
            <button type="button" className="tw-w-full tw-text-center tw-text-sm tw-font-bold tw-py-3 tw-bg-black tw-text-white tw-border tw-border-solid tw-border-black " onClick={onClose}>
              MODIFY THIS RING
            </button>
            {/* Other popoular combinations */}
          </div>
        </div>
        <div className="tw-pb-6">
          <p className="tw-text-center tw-font-normal tw-font-[18px] tw-mb-4">
            Other popular combinations
          </p>
          <div className="tw-flex tw-gap-14 tw-overflow-x-hidden tw-px-2 tw-justify-center tw-overflow-hidden">
            {(similarProducts.length ? similarProducts : []).map((item: any) => (
              <div
                key={item.ring_options_url} // use unique string from url or item.title if you want
                className="tw-flex-[0_0_160px] tw-text-center tw-cursor-pointer"
                onClick={() =>
                  navigateToUrl(item?.ring_options_url || bestSellerProduct?.ring_options_url, false, item.ring_options_url)
                }
                title={item.title}
              >
                <div className="tw-w-full tw-h-[140px] tw-flex tw-items-center tw-justify-center">
                  <img src={`${process.env.REACT_APP_ASSET_URL}${item.ring_image_url}`} alt={item.title || "ring"} className="tw-object-contain tw-h-full" />
                </div>
                <div className="tw-mt-2 tw-text-sm tw-font-normal tw-font-[18px]"> {item.price} </div>
              </div>
            ))}
          </div>
        </div>
      </If>
      <Loading timedOut={isFetching} />
    </div>
  );
};

export default KayDiscountedPopup;